<template>
	<div class="offer-verified__header">
		<OfferVerifiedHotelName
			:name="offerData.Name"
			:category="offerData.Category"
			:client="client"
		/>
	</div>
	<div class="offer-verified__price-details-container">
		<div
			v-if="isVerified && !isNotAvailable"
			class="offer-verified__price-detail-container"
		>
			<PriceDetails :offer-data="offerData" />
		</div>
		<OfferVerifiedFooter
			v-if="ibeCheckoutUrl && !isNotAvailable"
			:ibe-checkout-url="ibeCheckoutUrl"
			:button-text="translate('offer.addToCart')"
		/>
		<div
			v-if="!isVerified || isNotAvailable"
			class="offer-price-operator"
		>
			<GroupedPriceInfo
				class="offer-price-operator__price"
				:currency="currency"
				:total-price="totalPrice"
				:subtext="perPersonText"
			/>
			<RoundedButton
				class="offer-price-operator__check-button"
				v-bind="gaSelectContentAttr({ cId: 'offerlist_unverifiedoffer_button' })"
				:disabled="isNotAvailable"
				@click.prevent="onOfferVerify"
			>
				{{ buttonText }}
			</RoundedButton>
		</div>
	</div>
</template>

<script lang="ts" setup>
import GroupedPriceInfo from '@lmt-rpb/Molecules/GroupedPriceInfo/GroupedPriceInfo.vue';

import OfferVerifiedHotelName from './OfferVerifiedHotelName.vue';
import type { MergedOfferItemData } from '@interfaces/offer';
import type { ClientType } from '@/interfaces/common';
import OfferVerifiedFooter from './OfferVerifiedFooter.vue';
import { computed } from 'vue';
import { getPriceTypeWording, getTotalPrice } from '@/js/utils/priceUtils';
import type { ClientCurrency } from '@global-js/constants';
import RoundedButton from '@lmt-rpb/RoundedButton/RoundedButton.vue';
import PriceDetails from '@lmt-rpb/PriceDetails/PriceDetails.vue';
import { useOfferVerifiedInjection } from '../injectionProps';
import { gaSelectContentAttr } from 'src/utils/analytics';
import { useTranslations } from 'src/storybook/src/composables/translations';

interface Props {
	offerData: MergedOfferItemData;
	client?: ClientType;
	ibeCheckoutUrl?: string;
}

const props = defineProps<Props>();

const emit = defineEmits(['verify-offer']);
const { translate } = useTranslations();
const { isFetching, isVerified, isNotAvailable } = useOfferVerifiedInjection();
const buttonText = computed(() => (isNotAvailable.value ? translate('offer.notAvailable') : translate('offer.verifyOffer')));
const currency = computed((): ClientCurrency => props.offerData.CurrencyCode as ClientCurrency);
const onOfferVerify = () => {
	if (isFetching.value || isNotAvailable.value) {
		return;
	}
	emit('verify-offer');
};

const totalPrice = computed((): number => {
	// Ensure we have a valid price even if Pricing is undefined
	if (!props.offerData.Pricing) {
		return props.offerData.Price || 0;
	}
	return getTotalPrice(props.offerData.Pricing);
});

const perPersonText = computed((): string => {
	// Handle case where Pricing might be undefined
	if (!props.offerData.Pricing) {
		return '';
	}

	const price = props.offerData.Pricing.Single || { Amount: props.offerData.Price || 0 };
	return getPriceTypeWording({
		type: props.offerData?.Pricing?.Type,
		perPersonPrice: price.Converted ?? price.Amount,
		currency: currency.value,
	});
});
</script>

<style lang="scss" scoped>
.offer-price-operator {
	text-align: right;

	&__price {
		width: auto;
	}

	&__check-button {
		margin-top: 1.6rem;
		width: 100%;

		@media (min-width: $breakpoint-small) {
			width: auto;
		}
	}
}

.offer-verified {
	&__price-details-container {
		display: flex;
		flex-direction: column;
		justify-content: space-between;
	}
}
</style>
